import { ScrollView, View } from 'react-native';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/contexts/StoreContext';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { adjustmentsTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { SliderAdjustments } from '@photosi/rn-components/tools/slider-adjustments/SliderAdjustments';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { styles } from './AdjustmentsDesktopView.style';
import { SecondaryButton } from '@photosi/rn-components/buttons/secondary-button/SecondaryButton';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { ADJUSTMENTS } from '@photosi/rn-components/tools/adjustments/Adjustments.types';
import { useAdjustmentsLogic } from '@photosi/rn-components/tools/adjustments/Adjustments.logic';
import { useCustomScrollbar } from 'src/hooks/use-custom-visible-scrollbar/useCustomVisibleScrollbar';
import { parseAnalyticsParamsByUserImageArea } from 'src/_shared/analytics.utils';

export const AdjustmentsDesktopView = observer(({ focusable }: { focusable?: boolean }) => {
  const {
    i18n,
    project: { activeUserImageArea },
  } = useStore();

  const {
    brightness,
    contrast,
    saturation,
    setBrightness,
    setContrast,
    setSaturation,
    onPressRestore,
    setAdjustments,
  } = useAdjustmentsLogic(activeUserImageArea?.userImage);

  const scrollViewRef = useCustomScrollbar();

  return (
    <ScrollView
      style={{ paddingHorizontal: 24, paddingBottom: 24 }}
      ref={scrollViewRef}
      showsVerticalScrollIndicator={true}
      persistentScrollbar={true}
      contentContainerStyle={{ gap: 16 }}
    >
      {/* BRIGHTNESS */}
      <>
        <TextLato style={styles.adjustmentTitle}>{i18n.t('adjustments:brightness')}</TextLato>
        <View style={styles.adjustmentContainer}>
          <SliderAdjustments
            focusGroup={FocusGroups.SECOND_LEVEL_OF_CONFIGURATION}
            focusOrder={1}
            focusable={focusable}
            focusId={adjustmentsTestID(TestIDs.BRIGHTNESS_SLIDER)}
            testID={adjustmentsTestID(TestIDs.BRIGHTNESS_SLIDER)}
            defaultValue={brightness}
            onChange={(value: number) => {
              setBrightness(value);
            }}
            onChangeEnd={(value: number) => {
              setAdjustments({ brightness: value, contrast, saturation });
              amplitude.trackEvent(AnalyticsEvents.modifyPhoto.adjustClick, {
                ...parseAnalyticsParamsByUserImageArea(activeUserImageArea),
                adjust_choice: ADJUSTMENTS.BRIGHTNESS,
              });
            }}
          />
          <TextLato style={styles.adjustmentValue}>{brightness}</TextLato>
        </View>
      </>
      {/* CONTRAST */}
      <>
        <TextLato style={styles.adjustmentTitle}>{i18n.t('adjustments:contrast')}</TextLato>
        <View style={styles.adjustmentContainer}>
          <SliderAdjustments
            focusGroup={FocusGroups.SECOND_LEVEL_OF_CONFIGURATION}
            focusOrder={2}
            focusable={focusable}
            focusId={adjustmentsTestID(TestIDs.CONTRAST_SLIDER)}
            testID={adjustmentsTestID(TestIDs.CONTRAST_SLIDER)}
            defaultValue={contrast}
            onChange={(value: number) => {
              setContrast(value);
            }}
            onChangeEnd={(value: number) => {
              setAdjustments({ brightness, contrast: value, saturation });
              amplitude.trackEvent(AnalyticsEvents.modifyPhoto.adjustClick, {
                ...parseAnalyticsParamsByUserImageArea(activeUserImageArea),
                adjust_choice: ADJUSTMENTS.CONTRAST,
              });
            }}
          />
          <TextLato style={styles.adjustmentValue}>{contrast}</TextLato>
        </View>
      </>
      {/* SATURATION */}
      <>
        <TextLato style={styles.adjustmentTitle}>{i18n.t('adjustments:saturation')}</TextLato>
        <View style={styles.adjustmentContainer}>
          <SliderAdjustments
            focusGroup={FocusGroups.SECOND_LEVEL_OF_CONFIGURATION}
            focusOrder={3}
            focusable={focusable}
            focusId={adjustmentsTestID(TestIDs.SATURATION_SLIDER)}
            testID={adjustmentsTestID(TestIDs.SATURATION_SLIDER)}
            defaultValue={saturation}
            onChange={(value: number) => {
              setSaturation(value);
            }}
            onChangeEnd={(value: number) => {
              setSaturation(value);
              setAdjustments({ brightness, contrast, saturation: value });
              amplitude.trackEvent(AnalyticsEvents.modifyPhoto.adjustClick, {
                ...parseAnalyticsParamsByUserImageArea(activeUserImageArea),
                adjust_choice: ADJUSTMENTS.SATURATION,
              });
            }}
          />
          <TextLato style={styles.adjustmentValue}>{saturation}</TextLato>
        </View>
      </>
      <View style={{ paddingTop: 8 }}>
        <SecondaryButton
          style={styles.ctaButtonSecondary}
          onPress={async () => {
            onPressRestore();
          }}
          focusId={adjustmentsTestID(TestIDs.RESTORE)}
          testID={adjustmentsTestID(TestIDs.RESTORE)}
          focusOrder={4}
          focusGroup={FocusGroups.SECOND_LEVEL_OF_CONFIGURATION}
          focusable={focusable}
          text={i18n.t('adjustments:restore')}
          textStyle={styles.ctaText}
        />
      </View>
    </ScrollView>
  );
});

AdjustmentsDesktopView.displayName = 'AdjustmentsDesktopView';
